import { create, all } from 'mathjs';
import { localizeDecimalSeparator, delocalizeDecimalSeparator } from 'sharedreact/utils/LanguageSupport';

const math = create(all);
const defaultPrecision: number = 5;

function compare (firstNumber: number, secondNumber: number, precision: number = defaultPrecision): number {
  const first: number = math.round(firstNumber, precision);
  const second: number = math.round(secondNumber, precision);
  return (first > second ? 1 : (first < second ? -1 : 0));
}

function isEqualTo (firstNumber: number, secondNumber: number, precision: number = defaultPrecision): boolean {
  return compare(firstNumber, secondNumber, precision) === 0;
}

function isLessThan (firstNumber: number, secondNumber: number, precision: number = defaultPrecision): boolean {
  return compare(firstNumber, secondNumber, precision) < 0;
}

function isLessThanOrEqualTo (firstNumber: number, secondNumber: number, precision: number = defaultPrecision): boolean {
  return compare(firstNumber, secondNumber, precision) <= 0;
}

function isGreaterThan (firstNumber: number, secondNumber: number, precision: number = defaultPrecision): boolean {
  return compare(firstNumber, secondNumber, precision) > 0;
}

function isGreaterThanOrEqualTo (firstNumber: number, secondNumber: number, precision: number = defaultPrecision): boolean {
  return compare(firstNumber, secondNumber, precision) >= 0;
}

function isNumber (valueString: string): boolean {
  // try to convert the string to a number
  try {
    math.number(delocalizeDecimalSeparator(valueString));
    return true;
  } catch (e) {
    return false;
  }
}

function numberToString (value: number | math.Fraction | math.BigNumber, precision: number, fixed: boolean = false): string {
  let valueString:string = '';
  if (fixed) {
    valueString = math.format(
      value,
      { notation: 'fixed', precision: precision }
    );
  } else {
    valueString = math.round(value, precision).toString();
  }

  return localizeDecimalSeparator(valueString);
}

function stringToNumber (value: string, precision: number = defaultPrecision): number | null {
  let numberValue: number | null = null;
  try {
    numberValue = math.number(delocalizeDecimalSeparator(value));
  } catch (e) {
    // do nothing
  }

  if (numberValue != null && precision !== -1) {
    numberValue = math.round(
      numberValue,
      precision
    );
  }

  return numberValue;
}

const NumberSupport = {
  defaultPrecision,
  compare,
  isEqualTo,
  isLessThan,
  isLessThanOrEqualTo,
  isGreaterThan,
  isGreaterThanOrEqualTo,
  isNumber,
  numberToString,
  stringToNumber
};

export {
  numberToString
};

export default NumberSupport;
